import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MousePointerClick, Sparkles } from "lucide-react";

import {
  allCategories,
  defaultValues,
  fitToSize,
  fitUnits,
  getPiece,
  piecesByCategory,
  quantityShortfalls,
  shortfallMessage,
  sizeProblems,
  textStyleOf,
  unitValues,
  withTextStyle,
  type OptionValue,
  type OptionValues,
  type Piece,
} from "@/data/catalog";
import { getOrder, isEditableStatus, listOrders, type StoredOrder } from "@/lib/orders";
import { useCategories, usePieces, useThemes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { registerReferral, track } from "@/lib/referral";
import { padOrderNumber } from "@/lib/format";
import { layersForConfig } from "@/lib/preview";
import { ALL_COLLAR_URLS } from "@/data/collars";
import { ALL_MOCKUP_URLS } from "@/data/mockups";
import { useConfigurator, type OrderLine } from "@/hooks/use-configurator";
import { Header } from "@/components/configurator/Header";
import { ProductPreview } from "@/components/configurator/ProductPreview";
import { CategoryTabs } from "@/components/configurator/CategoryTabs";
import { ThemeFilter } from "@/components/configurator/ThemeFilter";
import { PieceCard } from "@/components/configurator/PieceCard";
import { PieceConfigurator } from "@/components/configurator/PieceConfigurator";
import { TextStyleToolbar } from "@/components/configurator/TextStyleToolbar";
import { OrderSummary } from "@/components/configurator/OrderSummary";
import { CheckoutModal } from "@/components/configurator/CheckoutModal";
import { MobileBottomBar } from "@/components/configurator/MobileBottomBar";
import { SiteFooter } from "@/components/SiteCredit";

/**
 * `?pedido=` abre um pedido; `?ref=<codigo>&utm_...` é o link de revendedor
 * digitado à mão (o mesmo efeito de `/r/<codigo>`).
 */
const SEARCH_KEYS = [
  "pedido",
  "ref",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;
type StoreSearch = Partial<Record<(typeof SEARCH_KEYS)[number], string>>;

export const Route = createFileRoute("/")({
  /**
   * `?pedido=<id>` abre a loja ja com um pedido carregado — e o que faz
   * "continuar montando" e "abrir copia no configurador" saírem da area do
   * cliente e chegarem aqui com o conteudo certo.
   */
  validateSearch: (search: Record<string, unknown>): StoreSearch => {
    const out: StoreSearch = {};
    for (const key of SEARCH_KEYS) {
      const value = search[key];
      if (typeof value === "string" && value) out[key] = value;
    }
    return out;
  },
  head: () => ({
    meta: [
      { title: "Uniformes e Camisetas Personalizadas — Monte o Seu Kit" },
      {
        name: "description",
        content:
          "Monte camisetas, calções, meiões, blusas, regatas e bermudas personalizadas: escolha tecido, gola, escudo, nome, número e patrocínios e veja o valor na hora.",
      },
      {
        property: "og:title",
        content: "Uniformes e Camisetas Personalizadas — Monte o Seu Kit",
      },
      {
        property: "og:description",
        content:
          "Configure cada peça do seu uniforme: tecido, gola, escudo, nome, número e patrocínios com preço em tempo real.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Index,
});

interface Draft {
  piece: Piece;
  values: OptionValues;
  quantity: number;
  /**
   * Qual linha da grade de peças (nome, número) o preview desenha.
   *
   * Fica no rascunho, fora de `values`: é escolha de visualização, e gravá-la
   * no pedido faria a produção ler uma "peça principal" que não existe.
   */
  preview: number;
  lineId?: string;
}

function Index() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const pedidoParam = search.pedido;

  const { profile, loading: sessionLoading } = useSession();
  const [activeCategory, setActiveCategory] = useState("");
  const [activeTheme, setActiveTheme] = useState("");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<StoredOrder | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  /** Grupo do texto cujo estilo o cliente está ajustando no preview. */
  const [styling, setStyling] = useState<string | null>(null);

  // Catálogo da API. Enquanto a resposta não chega, o registro em memória
  // ainda responde com o catálogo compartilhado — a tela nunca fica vazia.
  // Quem alimenta o registro é o próprio hook, no instante em que a resposta
  // chega: fazer isso aqui, em um efeito, era tarde demais (ver queries.ts).
  const categoriesQuery = useCategories();
  const piecesQuery = usePieces();
  const themes = useThemes().data ?? [];

  const categories = categoriesQuery.data ?? allCategories();
  useEffect(() => {
    // Primeira categoria disponível — sem chutar um id que pode não existir.
    if (!activeCategory && categories[0]) setActiveCategory(categories[0].id);
  }, [activeCategory, categories]);

  // Um tema que saiu do catálogo não pode deixar a loja presa em um filtro
  // invisível, mostrando nada.
  useEffect(() => {
    if (activeTheme && themes.length > 0 && !themes.some((t) => t.id === activeTheme)) {
      setActiveTheme("");
    }
  }, [activeTheme, themes]);

  /**
   * Trocar o tema leva para a primeira categoria que tem peça dele, se a aba
   * atual não tiver nenhuma — escolher "Religiosa" e cair em "Meiões" vazio
   * pareceria que o tema não existe.
   */
  const changeTheme = (themeId: string) => {
    setActiveTheme(themeId);
    if (!themeId || piecesByCategory(activeCategory, themeId).length > 0) return;
    const withPieces = categories.find((c) => piecesByCategory(c.id, themeId).length > 0);
    if (withPieces) setActiveCategory(withPieces.id);
  };

  // Os pedidos moram na conta: sem sessão não há o que contar no menu.
  const ordersQuery = useQuery({
    queryKey: ["store", "orders"],
    queryFn: listOrders,
    enabled: profile !== null,
  });
  const orders = ordersQuery.data ?? [];

  const {
    lines,
    layers,
    total,
    totalUnits,
    rules,
    context,
    contextWith,
    leadTimeDays,
    setLeadTimeDays,
    addLine,
    updateLine,
    duplicateLine,
    removeLine,
    clear,
    loadLines,
  } = useConfigurator();

  const refreshOrders = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["store", "orders"] });
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  }, [queryClient]);

  const countByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const line of lines) {
      counts[line.piece.category] =
        (counts[line.piece.category] ?? 0) + line.quantity;
    }
    return counts;
  }, [lines]);

  const visiblePieces = piecesByCategory(activeCategory, activeTheme);

  const countByPiece = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const line of lines) {
      counts[line.piece.id] = (counts[line.piece.id] ?? 0) + line.quantity;
    }
    return counts;
  }, [lines]);

  /**
   * Menor quantidade que uma linha da peça pode ter.
   *
   * O mínimo é da PEÇA no pedido, então as outras linhas dela contam: quem já
   * pôs 8 camisetas masculinas pode abrir uma linha de 2 femininas.
   */
  const lineMinimum = (piece: Piece, lineId?: string) => {
    const others = lines
      .filter((line) => line.piece.id === piece.id && line.lineId !== lineId)
      .reduce((sum, line) => sum + line.quantity, 0);
    return Math.max(1, (piece.minQuantity ?? 0) - others);
  };

  /**
   * O que trava o fechamento, com a frase do que falta: peça abaixo do mínimo
   * e peça sem tamanho (pedido salvo antes de a peça ter tamanhos, ou cuja
   * gola deixou de existir em algum tamanho escolhido).
   */
  const blockers = useMemo(
    () => [
      ...quantityShortfalls(
        lines.map((line) => ({ pieceId: line.piece.id, quantity: line.quantity })),
        getPiece,
      ).map(shortfallMessage),
      ...new Set(
        lines.flatMap((line) => sizeProblems(line.piece, line.values, line.quantity)),
      ),
    ],
    [lines],
  );

  const openCheckout = () => {
    // A barra do celular não mostra o aviso do resumo; sem isto o botão
    // abriria um checkout que o servidor recusaria no último passo.
    if (blockers.length > 0) {
      toast.error(blockers.join(". "));
      return;
    }
    setCheckoutOpen(true);
  };

  /**
   * O preview mostra o KIT montado: todas as peças já adicionadas mais a
   * peça em configuração (a linha em edição é substituída pelo rascunho).
   */
  const previewLayers = useMemo(() => {
    // Só o texto da peça EM CONFIGURAÇÃO se edita no preview: o das peças já
    // no pedido perde a marca de editável (para mexer nele, edita-se a linha).
    const fixed = layers.map(({ styleGroup: _locked, ...layer }) => layer);
    if (!draft) return fixed;
    const committed = draft.lineId
      ? fixed.filter((l) => !l.key.startsWith(draft.lineId!))
      : fixed;
    // Só o nome e o número da peça escolhida no olho vão para o desenho.
    const row = Math.min(draft.preview, draft.quantity - 1);
    const shown = unitValues(draft.piece, draft.values, row);
    return [
      ...committed,
      ...layersForConfig(draft.piece, shown, "draft"),
    ].sort((a, b) => a.order - b.order);
  }, [draft, layers]);

  /**
   * Abre o pedido pedido pela URL.
   *
   * Roda uma vez por id: depois de carregar, o parametro sai da URL para um
   * F5 nao recarregar por cima do que a pessoa ja mexeu.
   */
  useEffect(() => {
    if (!pedidoParam || sessionLoading) return;
    if (!profile) {
      toast.info("Entre na sua conta para abrir esse pedido.");
      void navigate({ to: "/", search: {}, replace: true });
      return;
    }
    let active = true;
    void (async () => {
      try {
        const order = await getOrder(pedidoParam);
        if (!active) return;
        loadLines(order.items);
        setLeadTimeDays(order.leadTimeDays ?? null);
        // Enviado, o pedido trava: o que abre aqui é uma cópia, que vira um
        // pedido novo ao salvar.
        if (isEditableStatus(order.status)) {
          setEditingOrder(order);
          toast.info(`Pedido #${padOrderNumber(order.number)} aberto para edição.`);
        } else {
          setEditingOrder(null);
          toast.info(
            `Pedido #${padOrderNumber(order.number)} já foi enviado — abrimos uma cópia para montar um novo.`,
          );
        }
      } catch {
        if (active) toast.error("Não foi possível abrir esse pedido.");
      } finally {
        if (active) void navigate({ to: "/", search: {}, replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [pedidoParam, sessionLoading, profile, loadLines, setLeadTimeDays, navigate]);

  // Link de revendedor montado à mão (`?ref=`): guarda a indicação e limpa a
  // URL, para um F5 ou um link compartilhado dali não contar outro acesso.
  useEffect(() => {
    if (!search.ref) return;
    void registerReferral(search.ref, undefined, {
      utmSource: search.utm_source,
      utmMedium: search.utm_medium,
      utmCampaign: search.utm_campaign,
      utmContent: search.utm_content,
    }).finally(() =>
      navigate({ to: "/", search: search.pedido ? { pedido: search.pedido } : {}, replace: true }),
    );
    // Roda uma vez por link aberto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.ref]);

  // Pré-carregamento: golas e mockups trocam sem flash.
  useEffect(() => {
    [...ALL_COLLAR_URLS, ...ALL_MOCKUP_URLS].forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  const handleClear = () => {
    clear();
    setDraft(null);
    setEditingOrder(null);
    toast.info("Configuração limpa. Comece de novo!");
  };

  const startNewOrder = () => {
    clear();
    setDraft(null);
    setEditingOrder(null);
    setCheckoutOpen(false);
  };

  // A barra de estilo só existe para um texto público da peça em
  // configuração; fechar a peça ou a caixa virar privada a fecha junto.
  const stylingGroup =
    draft?.piece.options.find((group) => group.id === styling && group.customText) ?? null;

  const editStyle = (groupId: string) => {
    setStyling(groupId);
    // No celular o preview fica acima das opções, fora da tela.
    document.getElementById("preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openConfigurator = (piece: Piece) => {
    setStyling(null);
    track("VISUALIZACAO", { pieceId: piece.id });
    setDraft({ piece, values: defaultValues(piece), quantity: lineMinimum(piece), preview: 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editLine = (line: OrderLine) => {
    setStyling(null);
    setDraft({
      piece: line.piece,
      values: { ...line.values },
      quantity: line.quantity,
      preview: 0,
      lineId: line.lineId,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changeDraftValue = (groupId: string, value: OptionValue) => {
    if (!draft) return;
    const changed = { ...draft.values, [groupId]: value };
    // A grade já não oferece o tamanho em que a gola escolhida não existe;
    // isto é a rede para o que escapar (pedido antigo, catálogo que mudou): a
    // gola cai na primeira que existe em todos os tamanhos, e o cliente é
    // avisado em vez de descobrir no resumo que ela mudou sozinha.
    const fitted = fitToSize(draft.piece, changed, draft.quantity);
    if (fitted !== changed) {
      const swapped = draft.piece.options
        .filter((group) => fitted[group.id] !== changed[group.id])
        .map((group) => group.label.toLowerCase());
      toast.info(`Ajustamos ${swapped.join(" e ")}: a opção escolhida não existe neste tamanho.`);
    }
    setDraft({ ...draft, values: fitted });
  };

  const confirmDraft = () => {
    if (!draft) return;
    // O rascunho guarda as linhas escondidas ao diminuir a quantidade; o
    // pedido leva só as peças que existem.
    const values = fitUnits(draft.piece, draft.values, draft.quantity);
    if (draft.lineId) {
      updateLine(draft.lineId, values, draft.quantity);
      toast.success(`${draft.piece.name} atualizada no pedido.`);
    } else {
      addLine(draft.piece.id, values, draft.quantity);
      track("CARRINHO", { pieceId: draft.piece.id, quantity: draft.quantity });
      toast.success(`${draft.piece.name} adicionada ao pedido!`);
    }
    setDraft(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header selectedCount={totalUnits} ordersCount={orders.length} />

      <>
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 pt-10 pb-8 sm:px-6 lg:pt-14">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/25 px-3.5 py-1.5 text-xs font-bold tracking-wide text-accent-foreground uppercase">
            <Sparkles className="h-3.5 w-3.5" />
            Configurador interativo
          </span>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold tracking-tight text-primary sm:text-5xl lg:text-6xl">
            Monte seu uniforme peça por peça.
          </h1>
          <p className="mt-3 max-w-xl text-base text-muted-foreground sm:text-lg">
            Escolha a peça, personalize tecido, gola, escudo, nome, número e
            patrocínios — e veja o kit sendo montado com o valor em tempo real.
          </p>
          <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-primary/70">
            <MousePointerClick className="h-4 w-4 text-accent" />
            Clique em “Personalizar” na peça desejada para começar.
          </p>
        </section>

        {/* Configurador */}
        <main className="mx-auto max-w-7xl px-4 pb-32 sm:px-6 lg:pb-16">
          {editingOrder && (
            <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-accent bg-accent/15 px-4 py-3">
              <p className="text-sm font-semibold text-accent-foreground">
                Editando o pedido #{padOrderNumber(editingOrder.number)} —
                salve para atualizar.
              </p>
              <button
                onClick={startNewOrder}
                className="shrink-0 text-xs font-bold text-primary underline underline-offset-2 hover:opacity-80"
              >
                Cancelar edição
              </button>
            </div>
          )}

          <div className="grid gap-8 lg:grid-cols-[1.05fr_1fr] xl:gap-12">
            {/* Coluna esquerda: preview + resumo (desktop) */}
            <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
              <ProductPreview
                layers={previewLayers}
                onClear={handleClear}
                selectedText={stylingGroup?.id ?? null}
                {...(draft ? { onSelectText: setStyling } : {})}
                toolbar={
                  draft && stylingGroup ? (
                    <TextStyleToolbar
                      group={stylingGroup}
                      style={textStyleOf(stylingGroup, draft.values)}
                      onChange={(style) =>
                        setDraft((d) =>
                          d ? { ...d, values: withTextStyle(d.values, stylingGroup, style) } : d,
                        )
                      }
                      onClose={() => setStyling(null)}
                    />
                  ) : null
                }
              />
              <OrderSummary
                lines={lines}
                total={total}
                rules={rules}
                context={context}
                blockers={blockers}
                leadTimeDays={leadTimeDays}
                onChangeLeadTime={setLeadTimeDays}
                onRemove={removeLine}
                onEditLine={editLine}
                onDuplicateLine={duplicateLine}
                onFinalize={openCheckout}
                className="mt-6 hidden lg:block"
              />
            </div>

            {/* Coluna direita: configuração da peça OU catálogo */}
            <div>
              {draft ? (
                <PieceConfigurator
                  piece={draft.piece}
                  values={draft.values}
                  quantity={draft.quantity}
                  minQuantity={lineMinimum(draft.piece, draft.lineId)}
                  previewIndex={draft.preview}
                  onChangePreview={(index) =>
                    setDraft((d) => (d ? { ...d, preview: index } : d))
                  }
                  context={contextWith(draft.quantity, draft.lineId)}
                  editing={Boolean(draft.lineId)}
                  onChangeValue={changeDraftValue}
                  onEditStyle={editStyle}
                  onChangeQuantity={(q) =>
                    setDraft((d) =>
                      d
                        ? { ...d, quantity: Math.max(lineMinimum(d.piece, d.lineId), q) }
                        : d,
                    )
                  }
                  onCancel={() => setDraft(null)}
                  onConfirm={confirmDraft}
                />
              ) : (
                <>
                  <ThemeFilter
                    themes={themes}
                    active={activeTheme}
                    onChange={changeTheme}
                  />
                  <CategoryTabs
                    categories={categories}
                    active={activeCategory}
                    onChange={setActiveCategory}
                    selectedCountByCategory={countByCategory}
                  />
                  <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {visiblePieces.length === 0 && activeTheme && (
                      <p className="col-span-full rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                        Nenhuma peça deste tema nesta categoria.{" "}
                        <button
                          type="button"
                          onClick={() => setActiveTheme("")}
                          className="font-semibold text-primary underline"
                        >
                          Ver todas
                        </button>
                      </p>
                    )}
                    {visiblePieces.map((piece) => (
                      <PieceCard
                        key={piece.id}
                        piece={piece}
                        count={countByPiece[piece.id] ?? 0}
                        onConfigure={openConfigurator}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Resumo (mobile, antes da barra fixa) */}
          <OrderSummary
            lines={lines}
            total={total}
            rules={rules}
            context={context}
            blockers={blockers}
            leadTimeDays={leadTimeDays}
            onChangeLeadTime={setLeadTimeDays}
            onRemove={removeLine}
            onEditLine={editLine}
            onDuplicateLine={duplicateLine}
            onFinalize={openCheckout}
            className="mt-8 lg:hidden"
          />
        </main>

        {/* No celular a barra de finalizar fica fixa embaixo: o crédito sobe. */}
        <SiteFooter className="pb-28 lg:pb-5" />

        <MobileBottomBar
          count={totalUnits}
          total={total}
          onFinalize={openCheckout}
        />
      </>

      <CheckoutModal
        open={checkoutOpen}
        lines={lines}
        layers={layers}
        total={total}
        context={context}
        editingOrder={editingOrder}
        onClose={() => setCheckoutOpen(false)}
        onSaved={(order) => {
          refreshOrders();
          if (isEditableStatus(order.status)) {
            setEditingOrder(order);
            toast.success(`Pedido #${padOrderNumber(order.number)} salvo na sua conta.`);
            return;
          }
          // Enviado, o pedido trava: a loja volta vazia para o próximo, e
          // continuar mexendo aqui não pode tentar regravar o que foi enviado.
          clear();
          setDraft(null);
          setEditingOrder(null);
          toast.success(`Pedido #${padOrderNumber(order.number)} enviado para a equipe!`);
        }}
        onEdit={() => setCheckoutOpen(false)}
        onNew={startNewOrder}
      />
    </div>
  );
}
